// ========================================
// MATH AGENT - FASE 4
// Motor Lógico Matemático LOMLOE 1º Primaria
// ========================================

/**
 * Agente Matemático
 * Genera estructuras matemáticas deterministas validadas según currículo LOMLOE.
 * Garantiza coherencia numérica antes de la narrativa.
 */

const PAEV_TYPES = {
    SUM: ['CA1', 'CO1'], // Cambio (Añadir), Combinación (Total)
    SUB: ['CA2'] // Cambio (Quitar) - CM1 (Comparación) Desactivado
};

/**
 * Genera array de 5 estructuras matemáticas puras
 * @param {Object} config - Configuración {problemType, level, context}
 * @returns {Array} Array de 5 problemas matemáticos sin narrativa
 */
function generateMathStructures(config, contextId = null) {
    console.log('🧮 [MathAgent] Generando estructuras matemáticas...');

    const structures = [];
    const { problemType, level } = config;
    const levelConfig = getLevelConfiguration(level);

    for (let i = 0; i < 5; i++) {
        // 1. Determinar Operación Base (Suma/Resta)
        let opBase = problemType;
        if (opBase === 'any') {
            opBase = Math.random() > 0.5 ? 'sum' : 'subtraction';
        }

        const internalOp = opBase === 'subtraction' ? 'resta' : 'suma';

        // 2. Seleccionar Subtipo PAEV
        const paevType = selectPaevType(internalOp);

        // 3. Generar Estructura
        let structure = generateSingleMathStructure(i + 1, internalOp, paevType, levelConfig, contextId);

        // 4. Validar cumplimiento LOMLOE (Reintentos)
        let attempts = 0;
        while (!validateLomloeCompliance(structure, levelConfig).valid && attempts < 5) {
            structure = generateSingleMathStructure(i + 1, internalOp, paevType, levelConfig, contextId);
            attempts++;
        }

        if (attempts >= 5) {
            console.warn(`⚠️ [MathAgent] Fallo al generar problema válido tras 5 intentos. Usando fallback seguro.`);
            structure = generateSafeFallback(i + 1, internalOp, levelConfig);
        }

        structures.push(structure);
    }

    console.log(`✅ [MathAgent] ${structures.length} estructuras generadas.`);
    return structures;
}

/**
 * Obtiene configuración LOMLOE según nivel
 */
function getLevelConfiguration(level) {
    const configs = {
        'beginner': {
            range: { min: 1, max: 10 },
            allowCarrying: false,
            useDistractors: false,
            maxLomloe: 19
        },
        'advanced': {
            range: { min: 10, max: 30 },
            allowCarrying: false,
            useDistractors: true,
            maxLomloe: 39
        },
        'expert': {
            range: { min: 20, max: 99 },
            allowCarrying: 'suma_only',
            useDistractors: true,
            maxLomloe: 99
        }
    };
    return configs[level] || configs['beginner'];
}

function selectPaevType(operation) {
    const types = operation === 'suma' ? PAEV_TYPES.SUM : PAEV_TYPES.SUB;
    return types[Math.floor(Math.random() * types.length)];
}

/**
 * Genera una estructura matemática individual
 */
function generateSingleMathStructure(id, op, paevType, levelConfig, contextId) {
    const range = levelConfig.range;
    let structure = {};

    if (op === 'suma') {
        structure = generateSumStructure(range, levelConfig, paevType);
    } else {
        structure = generateSubtractionStructure(range, levelConfig, paevType);
    }

    // Campos comunes
    structure.id = id;
    structure.logic_type = op;
    structure.paev_type = paevType; // Specific subtype (CA1, CO1...)
    structure.required_object_type = selectRandomObject(contextId);
    structure.distractors = generateDistractors(structure.correct_answer, 3, range, levelConfig.useDistractors);

    return structure;
}

/**
 * Genera estructura de suma
 */
function generateSumStructure(range, levelConfig, paevType) {
    let num1, num2, answer;

    // Algoritmo simple: Generar dos números que sumados no pasen del maxLomloe
    // Y si allowCarrying es false, verificar que (u1 + u2) < 10

    let valid = false;
    let attempts = 0;

    while (!valid && attempts < 50) {
        attempts++;
        // Estrategia: elegimos Total primero para asegurar rango, luego partimos? 
        // O elegimos partes? Elegir partes es mas natural para suma.

        num1 = randomInRange(range.min, Math.floor(levelConfig.maxLomloe * 0.6));
        num2 = randomInRange(range.min, Math.floor(levelConfig.maxLomloe * 0.6));
        answer = num1 + num2;

        if (answer > levelConfig.maxLomloe) continue;

        if (levelConfig.allowCarrying === false) {
            if ((num1 % 10) + (num2 % 10) >= 10) continue; // Requiere llevada
        }

        valid = true;
    }

    return {
        operands: [num1, num2],
        correct_answer: answer,
        explanation: `Suma (${paevType}): ${num1} + ${num2} = ${answer}`
    };
}

/**
 * Genera estructura de resta
 */
function generateSubtractionStructure(range, levelConfig, paevType) {
    let total, subtract, answer;
    let valid = false;
    let attempts = 0;

    while (!valid && attempts < 50) {
        attempts++;
        total = randomInRange(range.min + 3, levelConfig.maxLomloe); // Minuendo
        subtract = randomInRange(range.min, total - 1); // Sustraendo (menor que total)
        answer = total - subtract;

        // Validar no negativas (redundante con logica arriba pero safe)
        if (answer < 0) continue;

        // Validar llevadas
        if (levelConfig.allowCarrying === false) {
            // Si la unidad del minuendo es menor que la del sustraendo, requiere llevada (prestamo)
            if ((total % 10) < (subtract % 10)) continue;
        }

        // Para Comparación (CM), a veces queremos que los números sean cercanos
        if (paevType === 'CM1' && Math.random() > 0.5) {
            // Forzar cercanía para "cuántos más"
            if (answer > 10) continue;
        }

        valid = true;
    }

    return {
        operands: [total, subtract],
        correct_answer: answer,
        explanation: `Resta (${paevType}): ${total} - ${subtract} = ${answer}`
    };
}

function generateSafeFallback(id, op, config) {
    if (op === 'suma') {
        return {
            id, logic_type: 'suma', paev_type: 'CA1', operands: [2, 3], correct_answer: 5, distractors: [4, 6], explanation: 'Fallback Sum', required_object_type: 'cosas'
        };
    } else {
        return {
            id, logic_type: 'resta', paev_type: 'CA2', operands: [5, 2], correct_answer: 3, distractors: [2, 4], explanation: 'Fallback Sub', required_object_type: 'cosas'
        };
    }
}

/**
 * Genera números distractores
 */
function generateDistractors(correctAnswer, count, range, enabled) {
    if (!enabled) return [];

    const distractors = new Set();
    // Estrategias de error común: +/- 1, +/- 10
    const strategies = [
        (n) => n + 1,
        (n) => n - 1,
        (n) => n + 10,
        (n) => n - 10,
        (n) => n + 2,
        (n) => n - 2
    ];

    // Intentar generar errores inteligentes
    strategies.forEach(strat => {
        const val = strat(correctAnswer);
        if (val > 0 && val !== correctAnswer && distractors.size < count) {
            distractors.add(val);
        }
    });

    // Rellenar con aleatorios si faltan
    while (distractors.size < count) {
        const d = randomInRange(Math.max(1, correctAnswer - 5), correctAnswer + 5);
        if (d !== correctAnswer && d > 0) distractors.add(d);
    }

    return Array.from(distractors);
}

function selectRandomObject(contextId = null) {
    // Fallback básico, el verdadero enriquecimiento ocurre en el NarrativeAgent
    // Pero si MathAgent necesita pasar una pista, puede hacerlo aquí
    if (contextId && window.CONTEXT_OBJECTS && window.CONTEXT_OBJECTS[contextId]) {
        // Logic reused or delegated
        return 'context_object';
    }
    return 'unidades';
}

function validateLomloeCompliance(structure, levelConfig) {
    if (!structure.operands) return { valid: false };
    // Basic checks are handled inside generators, this is a final safety net
    return { valid: true };
}

function randomInRange(min, max) {
    return Math.floor(Math.random() * (max - min + 1)) + min;
}

if (typeof window !== 'undefined') {
    window.generateMathStructures = generateMathStructures;
    window.validateLomloeCompliance = validateLomloeCompliance;
}

console.log('✅ [MathAgent v2] Módulo PAEV cargado');
